import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchEmployees,
  fetchEmployeeById,
  createEmployee,
  updateEmployee,
  searchEmployees,
  fetchNonEmployeeStaff,
  fetchUsersWithAccess,
  getAllEmployees,
  fetchEmployeesWithManagementPermission,
} from '../actions/employeeActions';
import { EmployeeState, EmployeeData } from '../types/employeeTypes';

const initialState: EmployeeState = {
  employees: [],
  currentEmployee: null,
  loading: false,
  error: null,
  pagination: null,
  searchResults: [],
  searching: false,
  nonEmployeeStaff: [], // Add new state for non-employee staff
  loadingNonEmployeeStaff: false,
  usersWithAccess: [], // Add new state for users with application access
  loadingUsersWithAccess: false, // Loading state for users with access
  potentialManagers: [], // Add new state for employees with management permission
  loadingPotentialManagers: false, // Loading state for potential managers
};

const employeeSlice = createSlice({
  name: 'employees',
  initialState,
  reducers: {
    clearEmployeeError: state => {
      state.error = null;
    },
    clearCurrentEmployee: state => {
      state.currentEmployee = null;
    },
    clearSearchResults: state => {
      state.searchResults = [];
      state.searching = false;
    },
  },
  extraReducers: builder => {
    // Handle fetchEmployees
    builder
      .addCase(fetchEmployees.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployees.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.employees = action.payload.data || [];
        state.pagination = action.payload.pagination || null;
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Failed to fetch employees';
      });

    // Handle getAllEmployees
    builder
      .addCase(getAllEmployees.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllEmployees.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.employees = action.payload.data || [];
      })
      .addCase(getAllEmployees.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Failed to fetch all employees';
      });

    // Handle fetchNonEmployeeStaff
    builder
      .addCase(fetchNonEmployeeStaff.pending, state => {
        state.loadingNonEmployeeStaff = true;
        state.error = null;
      })
      .addCase(fetchNonEmployeeStaff.fulfilled, (state, action: PayloadAction<any>) => {
        state.loadingNonEmployeeStaff = false;
        state.nonEmployeeStaff = action.payload.data || [];
      })
      .addCase(fetchNonEmployeeStaff.rejected, (state, action) => {
        state.loadingNonEmployeeStaff = false;
        state.error = (action.payload as string) || 'Failed to fetch non-employee staff';
      });

    // Handle fetchEmployeeById
    builder
      .addCase(fetchEmployeeById.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeById.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.currentEmployee = action.payload.data || null;
      })
      .addCase(fetchEmployeeById.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Failed to fetch employee';
      });

    // Handle createEmployee
    builder
      .addCase(createEmployee.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createEmployee.fulfilled, state => {
        state.loading = false;
        // We don't need to update state here as we'll reload the list
      })
      .addCase(createEmployee.rejected, (state, action) => {
        state.loading = false;
        // Store the complete error object or just the message string
        state.error = (action.payload as string) || 'Failed to create employee';
      });

    // Handle updateEmployee
    builder
      .addCase(updateEmployee.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEmployee.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        if (state.currentEmployee && action.payload.data) {
          state.currentEmployee = action.payload.data;
        }
      })
      .addCase(updateEmployee.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Failed to update employee';
      });

    // Handle searchEmployees
    builder
      .addCase(searchEmployees.pending, state => {
        state.searching = true;
        state.error = null;
      })
      .addCase(searchEmployees.fulfilled, (state, action: PayloadAction<any>) => {
        state.searching = false;
        state.searchResults = action.payload.data || [];
      })
      .addCase(searchEmployees.rejected, (state, action) => {
        state.searching = false;
        state.error = (action.payload as string) || 'Failed to search employees';
      });

    // Handle fetchUsersWithAccess
    builder
      .addCase(fetchUsersWithAccess.pending, state => {
        state.loadingUsersWithAccess = true;
        state.error = null;
      })
      .addCase(fetchUsersWithAccess.fulfilled, (state, action: PayloadAction<any>) => {
        state.loadingUsersWithAccess = false;
        state.usersWithAccess = action.payload.data || [];
      })
      .addCase(fetchUsersWithAccess.rejected, (state, action) => {
        state.loadingUsersWithAccess = false;
        state.error = (action.payload as string) || 'Failed to fetch users with access';
      });

    // Handle fetchEmployeesWithManagementPermission
    builder
      .addCase(fetchEmployeesWithManagementPermission.pending, state => {
        state.loadingPotentialManagers = true;
        state.error = null;
      })
      .addCase(fetchEmployeesWithManagementPermission.fulfilled, (state, action: PayloadAction<any>) => {
        state.loadingPotentialManagers = false;
        state.potentialManagers = action.payload.data || [];
      })
      .addCase(fetchEmployeesWithManagementPermission.rejected, (state, action) => {
        state.loadingPotentialManagers = false;
        state.error = (action.payload as string) || 'Failed to fetch potential managers';
      });
  },
});

export const { clearEmployeeError, clearCurrentEmployee, clearSearchResults } =
  employeeSlice.actions;

export default employeeSlice.reducer;
